import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { encryptionService } from '../utils/encryption';
import { isWithinPostLoginGracePeriod } from '../utils/auth';
import { API_BASE_URL } from './ApiUrls';

/**
 * Enhanced API service with built-in encryption support
 * Automatically handles encrypted requests and responses
 */
export class EncryptedApiService {
  private static instance: EncryptedApiService;
  private axiosInstance: AxiosInstance;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    this.setupInterceptors();
  }

  public static getInstance(): EncryptedApiService {
    if (!EncryptedApiService.instance) {
      EncryptedApiService.instance = new EncryptedApiService();
    }
    return EncryptedApiService.instance;
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        return this.handleRequest(config);
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return this.handleResponse(response);
      },
      (error) => {
        return this.handleError(error);
      }
    );
  }

  /**
   * Handle outgoing requests
   */
  private handleRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    try {
      // Add authentication token if available
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }

      // Handle encryption for POST/PUT requests
      if (this.shouldEncryptRequest(config) && config.data) {
        const encryptedRequest = encryptionService.createEncryptedRequest(config.data);
        config.data = encryptedRequest;
        
        config.headers.set('X-Encrypted', 'true');
      }

      console.log('API Request:', {
        url: config.url,
        method: config.method,
        encrypted: config.headers?.['X-Encrypted'] === 'true'
      });

    } catch (error) {
      console.error('Request processing failed:', error);
    }

    return config;
  }

  /**
   * Handle incoming responses
   */
  private handleResponse(response: AxiosResponse): AxiosResponse {
    try {
      const isEncrypted = response.headers['x-encrypted'] === 'true' || 
                         encryptionService.isEncryptedResponse(response.data);

      if (isEncrypted && encryptionService.isEnabled()) {
        console.log('Decrypting encrypted response:', response.config.url);
        
        const decryptedData = encryptionService.decryptResponse(response.data);
        response.data = decryptedData;
        
        // Remove encryption header
        delete response.headers['x-encrypted'];
        
        console.log('Response decrypted successfully');
      }

      // Handle authentication errors
      if (response.status === 401) {
        this.handleAuthError('Session expired. Please login again.');
      } else if (response.status === 403) {
        this.handleAuthError('You do not have permission to access this resource');
      }

    } catch (error) {
      console.error('Response processing failed:', error);
    }

    return response;
  }

  /**
   * Handle response errors
   */
  private handleError(error: any): Promise<any> {
    try {
      // Check if error response is encrypted
      if (error.response?.data && encryptionService.isEncryptedResponse(error.response.data)) {
        const decryptedError = encryptionService.decryptResponse(error.response.data);
        error.response.data = decryptedError;
      }

      // Handle authentication errors
      if (error.response?.status === 401) {
        this.handleAuthError('Session expired. Please login again.');
      } else if (error.response?.status === 403) {
        this.handleAuthError('You do not have permission to access this resource');
      }

    } catch (decryptionError) {
      console.error('Error response decryption failed:', decryptionError);
    }

    return Promise.reject(error);
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(messageText: string): void {
    console.warn('Authentication failed:', messageText);
    if (!isWithinPostLoginGracePeriod()) {
      message.error(messageText);
    }
  }

  /**
   * Check if request should be encrypted
   */
  private shouldEncryptRequest(config: InternalAxiosRequestConfig): boolean {
    // For now, disable request encryption to avoid 500 errors
    // Backend will handle response encryption automatically when enabled
    return false; // encryptionService.isEnabled() && !shouldSkip && (config.method === 'post' || config.method === 'put') && config.data !== null && config.data !== undefined;
  }

  /**
   * Extract error message from API response
   */
  private extractErrorMessage(error: any, fallback = 'An error occurred'): string {
    try {
      if (error?.response?.data?.message) {
        return error.response.data.message;
      }
      if (error?.message) {
        return error.message;
      }
      if (typeof error === 'string') {
        return error;
      }
    } catch (e) {
      console.error('Error parsing error message:', e);
    }

    return fallback;
  }

  // ==================== PUBLIC API METHODS ====================

  /**
   * Make GET request
   */
  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      console.log(`🔍 GET ${url}`);
      const response = await this.axiosInstance.get(url, config);
      return response.data;
    } catch (error) {
      console.error(`GET ${url} failed:`, error);
      throw new Error(this.extractErrorMessage(error));
    }
  }

  /**
   * Make POST request
   */
  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      console.log(`📝 POST ${url}`, data);
      const response = await this.axiosInstance.post(url, data, config);
      return response.data;
    } catch (error) {
      console.error(`POST ${url} failed:`, error);
      throw new Error(this.extractErrorMessage(error));
    }
  }

  /**
   * Make PUT request
   */
  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      console.log(`✏️ PUT ${url}`, data);
      const response = await this.axiosInstance.put(url, data, config);
      return response.data;
    } catch (error) {
      console.error(`PUT ${url} failed:`, error);
      throw new Error(this.extractErrorMessage(error));
    }
  }

  /**
   * Make DELETE request
   */
  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      console.log(`🗑️ DELETE ${url}`);
      const response = await this.axiosInstance.delete(url, config);
      return response.data;
    } catch (error) {
      console.error(`DELETE ${url} failed:`, error);
      throw new Error(this.extractErrorMessage(error));
    }
  }

  /**
   * Make PATCH request
   */
  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      console.log(`🔧 PATCH ${url}`, data);
      const response = await this.axiosInstance.patch(url, data, config);
      return response.data;
    } catch (error) {
      console.error(`PATCH ${url} failed:`, error);
      throw new Error(this.extractErrorMessage(error));
    }
  }

  /**
   * Get encryption status
   */
  public getEncryptionStatus(): { enabled: boolean; hasKey: boolean } {
    return {
      enabled: encryptionService.isEnabled(),
      hasKey: !!process.env.REACT_APP_ENCRYPTION_KEY
    };
  }

  /**
   * Test encryption/decryption
   */
  public async testEncryption(): Promise<{ success: boolean; message: string }> {
    try {
      const testData = { message: 'Test encryption', timestamp: Date.now() };
      const encryptedData = encryptionService.encrypt(JSON.stringify(testData));
      const decrypted = encryptionService.decrypt(encryptedData);
      
      return {
        success: JSON.stringify(testData) === decrypted,
        message: 'Encryption test completed'
      };
    } catch (error) {
      return {
        success: false,
        message: `Encryption test failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }
}

// Export singleton instance
export const encryptedApiService = EncryptedApiService.getInstance();
